import React, { useMemo } from 'react';

import { useChildComponent } from '@gogame/hooks-ts';
import DataGrid from '@gogame/data-grid';
import {
  ScreenLink,
  useScreenDispatch,
  useScreenState,
  useScreenTitle,
} from '@gogame/screens';

import {
  COLUMN_ID,
  COLUMN_NAME,
  COLUMN_STATUS,
  COLUMN_PARENT,
  COLUMN_PARTNER,
  COLUMN_PARTNER_API_ID,
  COLUMN_COUNTRIES,
  COLUMN_APPROXIMATE_USERS,
  COLUMN_LAST_SYNC,
  COLUMN_ACCOUNT,
  COLUMN_CREATED_BY,
  COLUMN_CREATED_DATE,
} from 'scenes/CustomAudiences/LookalikeAudiences/constants';
import { useLoadLookalikeAudienceTable } from 'scenes/CustomAudiences/LookalikeAudiences/data';
import {
  SET_TABLE_SETTINGS,
  COLLAPSE_COLUMNS,
  REORDER_COLUMNS,
} from 'scenes/CustomAudiences/LookalikeAudiences/screenReducer';
import PartnerAudienceDetails from 'scenes/CustomAudiences/PartnerAudiences/components/PartnerAudienceDetails';
import { lookalikeAudiencesDetailsLink } from 'scenes/CustomAudiences/LookalikeAudiences/navigation';
import { getColor } from 'scenes/CustomAudiences/LookalikeAudiences/components/Status';
import LookalikeAudienceActions from 'scenes/CustomAudiences/LookalikeAudiences/components/Actions';

import Toolbar from './Toolbar';
import BatchToolbar from './BatchToolbar';
import Parent from './Parent';
import { Stack, Typography } from '@gogame/components';

const AudiencesDataTable = () => {
  const [screenTitle] = useScreenTitle();
  const dispatch = useScreenDispatch();
  const screenState = useScreenState();
  const [childComponent, openChildComponent] = useChildComponent();

  const params = useMemo(() => {
    const { filters, tableSettings, searchQuery } = screenState;
    return {
      filters,
      search: searchQuery,
      ...tableSettings,
    };
  }, [screenState]);
  const { data, isLoading, isRefetching } =
    useLoadLookalikeAudienceTable(params);

  const handleChangeSettings = (settings) => {
    dispatch({
      type: SET_TABLE_SETTINGS,
      payload: settings,
    });
  };

  const handleReorder = (order) => {
    dispatch({
      type: REORDER_COLUMNS,
      payload: order,
    });
  };

  const handleCollapse = (collapsed) => {
    dispatch({
      type: COLLAPSE_COLUMNS,
      payload: collapsed,
    });
  };

  const handleOpenPartnerAudience = (_e, { id }) => {
    openChildComponent(PartnerAudienceDetails, { id });
  };

  return (
    <React.Fragment>
      <DataGrid
        rows={data?.rows}
        count={data?.count}
        title={screenTitle}
        loading={isLoading}
        fetching={isRefetching}
        onChangeSettings={handleChangeSettings}
        onReorder={handleReorder}
        onCollapse={handleCollapse}
        tableSettings={screenState.tableSettings}
        columnsState={screenState.columns}
        storageKey={'audiences'}
        defaultColumnWidth={160}
        checkboxSelection={true}
        toolbar={<Toolbar />}
        selectedRowsToolbarActions={({ selectedValues }) => (
          <BatchToolbar
            selectedValues={selectedValues}
            openChildComponent={openChildComponent}
          />
        )}
      >
        <DataGrid.Column
          field={COLUMN_ID}
          type={'text'}
          headerName={'ID'}
          width={90}
          fixed={true}
          resizable={false}
          align={'right'}
          headerAlign={'right'}
        />
        <DataGrid.Column
          field={COLUMN_NAME}
          type={'text'}
          headerName={'Name'}
          width={322}
          fixed={true}
          resizable={false}
          formatter={(value, { id }) => (
            <ScreenLink
              link={lookalikeAudiencesDetailsLink}
              pathParams={{ id }}
            >
              {value}
            </ScreenLink>
          )}
        />
        <DataGrid.Column
          headerName={'Status'}
          field={COLUMN_STATUS}
          width={110}
          type={'status'}
          getStatusColor={getColor}
        />
        <DataGrid.Column
          field={COLUMN_APPROXIMATE_USERS}
          type={'number'}
          headerName={'Size'}
          width={104}
        />
        <DataGrid.Column
          field={COLUMN_PARENT}
          headerName={'Partner Audience / Campaigns / Adsets'}
          width={278}
          sortable={false}
          renderCell={({ row }) => (
            <Parent onClick={handleOpenPartnerAudience} row={row} />
          )}
        />
        <DataGrid.Column
          field={COLUMN_PARTNER}
          headerName={'Partner'}
          type={'text'}
          width={128}
          getValue={(v) => v?.name}
        />
        <DataGrid.Column
          field={COLUMN_PARTNER_API_ID}
          headerName={'Partner Api ID'}
          type={'text'}
          width={133}
          sortable={false}
        />
        <DataGrid.Column
          field={COLUMN_COUNTRIES}
          headerName={'Countries'}
          type={'list'}
          unitsLabel={'Countries'}
          limit={1}
          placeholder={'All'}
          sortable={false}
        />
        <DataGrid.Column
          field={COLUMN_ACCOUNT}
          headerName={'Account'}
          width={177}
          formatter={(value) =>
            value?.id ? (
              <Stack zeroMinWidth={true}>
                <Typography variant={'body2'} noWrap={true}>
                  {value.name}
                </Typography>
                <Typography variant={'body2'} noWrap={true}>
                  ID {value.id}
                </Typography>
              </Stack>
            ) : null
          }
        />
        <DataGrid.Column
          field={COLUMN_LAST_SYNC}
          headerName={'Last Sync'}
          type={'date'}
          template={'LLL'}
          timeZone={'local'}
          sortable={false}
        />
        <DataGrid.Column
          field={COLUMN_CREATED_BY}
          headerName={'Created by'}
          type={'text'}
          getValue={(v) => v?.name}
        />
        <DataGrid.Column
          field={COLUMN_CREATED_DATE}
          headerName={'Created'}
          type={'date'}
          template={'LLL'}
          timeZone={'local'}
        />
        <DataGrid.Column
          actionsColDef
          type={'actions'}
          limit={2}
          onShowModal={openChildComponent}
          actionsComponent={LookalikeAudienceActions}
        />
      </DataGrid>
      {childComponent}
    </React.Fragment>
  );
};

export default AudiencesDataTable;
